/* eslint-disable @typescript-eslint/no-explicit-any */
//import '@adyen/adyen-web/dist/adyen.css'

import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types'
import { Skeleton, Stack, useDisclosure } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { redirect } from 'redux-first-router'

import {
  AdditionalPaymentDetails,
  fetchPaymentMethods,
  makePayment,
  makePaymentDetails,
  PaymentState,
} from '../../api/payment'
import { isServer } from '../../utils'
import { Page } from '../../utils/enums'

interface AdyenWebDropInProps {
  apiBaseUrl: string
  apiKey: string
  customerContextId: string
  documentId: string
  isProductionEnvironment: boolean
  locale: string
  returnUrl: string
}

interface DropInHandler {
  handleAction: (arg0: any) => void
  setStatus: (status: string, payload?: { message: string }) => void
}

const redirectToPageWithQuery = (dispatch: Dispatch<AnyAction>) => (
  url: string,
  query: any,
  payload?: any,
) => {
  dispatch(
    redirect({
      payload,
      query,
      type: url,
    }),
  )
}

interface AdyenConfiguration extends CoreOptions {
  onAdditionalDetails: (
    state: { data: AdditionalPaymentDetails },
    dropin: DropInHandler,
  ) => void
  onError: (error: any) => void
  onSubmit: (state: PaymentState, dropin: DropInHandler) => void
}

export const AdyenWebDropIn = ({
  apiBaseUrl,
  apiKey,
  customerContextId,
  documentId,
  isProductionEnvironment,
  locale,
  returnUrl,
}: AdyenWebDropInProps) => {
  const dispatch = useDispatch()
  const redirectToPaymentCallBackUrl = redirectToPageWithQuery(dispatch)
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true })
  const environment = isProductionEnvironment ? 'live-au' : 'test'

  const configuration: AdyenConfiguration = {
    // Web Drop-in versions before 3.10.1 use originKey instead of clientKey.
    clientKey: apiKey,
    environment,
    locale,

    onAdditionalDetails: async state => {
      if (state) {
        state.data.paymentId = localStorage.getItem('paymentId') || ''
        const { error, paymentDetails } = await makePaymentDetails(
          apiBaseUrl,
          state.data,
        )
        if (error) {
          // do we need to handle error any differently ?
          redirectToPaymentCallBackUrl(
            Page.PaymentInvoiceRedirect,
            {
              ...paymentDetails,
              status: 'Error',
            },
            { documentId },
          )
        }
        // redirect to success page with the query string built of the params
        // in case of paypal method
        redirectToPaymentCallBackUrl(
          Page.PaymentInvoiceRedirect,
          paymentDetails,
          { documentId },
        )
      }
    },
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error)
    },
    onSubmit: async (state, dropin) => {
      dropin.setStatus('loading')
      const { error, paymentResponse } = await makePayment(apiBaseUrl, {
        ...state,
        customerContextId,
        documentId,
        origin: window.location.origin,
        returnUrl,
      })
      if (error) {
        // do we need to handle error any differently ?
        redirectToPaymentCallBackUrl(
          Page.PaymentInvoiceRedirect,
          {
            ...paymentResponse,
            status: 'Error',
          },
          { documentId },
        )
      }

      if (paymentResponse?.paymentId) {
        localStorage.setItem('paymentId', paymentResponse.paymentId)
      }

      if (paymentResponse?.action) {
        // Drop-in handles the action object from the /payments response
        dropin.handleAction(paymentResponse.action)
      } else {
        dropin.setStatus('success', { message: 'Payment successful!' })
        // redirect to success page with the query string built of the params
        redirectToPaymentCallBackUrl(
          Page.PaymentInvoiceRedirect,
          paymentResponse,
          { documentId },
        )
      }
    },
  }

  useEffect(() => {
    const mountDropIn = async () => {
      const { paymentMethods } = await fetchPaymentMethods({
        apiBaseUrl,
        customerContextId,
        params: { documentId },
      })

      if (paymentMethods) {
        configuration.paymentMethodsResponse = paymentMethods
        configuration.paymentMethodsConfiguration =
          paymentMethods.paymentMethodsConfiguration

        const AdyenCheckout = require('@adyen/adyen-web')

        new AdyenCheckout(configuration)
          .create('dropin')
          .mount('#adyen-dropin-container')

        onClose()
      }
    }

    if (!isServer()) {
      mountDropIn()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {isOpen && (
        <Stack>
          <Skeleton height="400px" />
          <Skeleton height="60px" />
          <Skeleton height="60px" />
        </Stack>
      )}
      <div id="adyen-dropin-container" />
    </>
  )
}
